import React from "react";
import { twMerge } from "tailwind-merge";

import { OverflowTooltip } from "@/components/overflow-tooltip";
import { UserAvatar } from "@/components/user-avatar";
import { useAuth } from "@/features/auth/shared/hooks/use-auth";
import {
  checkPlanIsUsageRestricted,
  getIdentityNameMap,
  getMaxBrowsingUsage,
  parsePlanName,
} from "@/features/stripe/utils/stripe-plan";
import { useLang } from "@/hooks/use-lang";
import { useGetServiceQuota } from "@/services/restriction/get-service-quota";

import { StarIcon } from "ui/icon/shared";

import { TipBadge } from "../TipBadge";

type Props = {
  className?: string;
};

export const UserInfo = ({ className }: Props) => {
  const { t } = useLang();
  const { isUnauthenticated } = useAuth();

  return (
    <div
      className={twMerge(
        "flex w-full items-center gap-x-3 px-4 py-2",
        className
      )}
    >
      <UserAvatar width={40} height={40} />
      {isUnauthenticated ? (
        <p className="text-sm text-gray-700">{t.layout.navbar.notLoggedIn}</p>
      ) : (
        <UserDetails />
      )}
    </div>
  );
};

const UserDetails = () => {
  const { t, lang } = useLang();
  const { profile } = useAuth();
  const name = profile?.name ?? "";
  const isEnterprise = profile?.authProvider === "Business";
  const currentPlan = profile?.plan ?? "Free";
  const showUsage = checkPlanIsUsageRestricted({ plan: currentPlan });
  const plan = getIdentityNameMap(t)[currentPlan];
  const [planName, planPeriod] = parsePlanName({ lang, planName: plan });

  const { data } = useGetServiceQuota();
  const maxBrowsingUsage = getMaxBrowsingUsage({ plan: currentPlan });
  const counts =
    data?.available !== undefined && data?.remains !== undefined
      ? Math.min(Math.max(data.available - data.remains, 0), maxBrowsingUsage)
      : 0;

  return (
    <div className="w-full max-w-[220px] space-y-2 truncate px-2 text-slate-600">
      <OverflowTooltip
        placement="top-start"
        title={name}
        wrapperClassName="break-words text-sm font-medium"
        childrenLabel="user name"
      >
        {name}
      </OverflowTooltip>
      {isEnterprise ? (
        <div className="flex items-center gap-1" aria-label="user plan">
          <p className="text-xs" aria-label="plan name">
            {t.stripe.plan.enterprise}
          </p>
          <StarBadge>{planName}</StarBadge>
        </div>
      ) : (
        <div className="space-y-2">
          <div className="flex items-center gap-2" aria-label="user plan">
            <p className="text-xs text-zinc-500">{planName}</p>
            {planPeriod && <StarBadge>{planPeriod}</StarBadge>}
          </div>
          {showUsage && (
            <p className="whitespace-pre-wrap text-xs leading-normal text-zinc-500">
              {lang === "zh" ? t.layout.navbar.usage : null}
              <TipBadge
                text={`${counts}/${maxBrowsingUsage}`}
                className="mx-1 border-[1px] border-yellow-200"
                aria-label="access count"
              />
              {lang === "en" ? t.layout.navbar.usage : null}
              {lang === "zh" ? t.layout.navbar.times : null}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

const StarBadge = ({ children }: { children: React.ReactNode }) => (
  <span className="flex select-none items-center gap-[2px] rounded-full border border-sky-200 bg-sky-100 px-1.5 py-0.5 text-xs font-medium text-sky-700">
    <StarIcon className="fill-sky-700" />
    {children}
  </span>
);
